import React, { useState, useEffect } from 'react';

const STATUS = {
  UNTESTED: 'untested',
  TESTING: 'testing',
  VALID: 'valid',
  INVALID: 'invalid',
};

const statusConfig = {
  [STATUS.UNTESTED]: { color: '#64748b', bg: 'rgba(100,116,139,0.15)', label: 'Not tested' },
  [STATUS.TESTING]: { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', label: 'Testing...' },
  [STATUS.VALID]: { color: '#22c55e', bg: 'rgba(34,197,94,0.15)', label: 'Connected' },
  [STATUS.INVALID]: { color: '#ef4444', bg: 'rgba(239,68,68,0.15)', label: 'Failed' },
};

const COMING_SOON = [
  {
    name: 'HUDL / Wyscout',
    description: 'Import match videos and player performance data directly from HUDL or Wyscout.',
    icon: 'HW',
    color: '#00b140',
  },
  {
    name: 'Opta Data',
    description: 'Access detailed event-level match data and advanced statistics from Opta.',
    icon: 'OP',
    color: '#1a73e8',
  },
  {
    name: 'InStat',
    description: 'Sync player and team analytics from InStat for deeper tactical insights.',
    icon: 'IS',
    color: '#e53935',
  },
];

const s = {
  card: {
    background: '#0f2040',
    border: '1px solid #1e3a5f',
    borderRadius: '12px',
    padding: '24px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#f1f5f9',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    background: '#0a1628',
    border: '1px solid #1e3a5f',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  inputFocused: {
    width: '100%',
    padding: '10px 14px',
    background: '#0a1628',
    border: '1px solid #f59e0b',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  btnPrimary: {
    background: '#f59e0b',
    color: '#0a1628',
    fontWeight: '600',
    padding: '10px 24px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    cursor: 'pointer',
  },
  btnSecondary: {
    background: 'transparent',
    border: '1px solid #1e3a5f',
    color: '#f1f5f9',
    fontWeight: '600',
    padding: '10px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  fieldGroup: {
    marginBottom: '16px',
  },
  comingSoonCard: {
    background: '#0a1628',
    border: '1px solid #1e3a5f',
    borderRadius: '10px',
    padding: '16px',
    opacity: 0.5,
    position: 'relative',
    overflow: 'hidden',
  },
  comingSoonBadge: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    fontSize: '10px',
    fontWeight: '700',
    padding: '3px 10px',
    borderRadius: '9999px',
    background: 'rgba(100, 116, 139, 0.2)',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  iconCircle: (color) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '10px',
  }),
};

function APIKeysTab({ profile, onSave, saving, testStatsBombConnection }) {
  const [sbUsername, setSbUsername] = useState('');
  const [sbToken, setSbToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(STATUS.UNTESTED);
  const [statusMessage, setStatusMessage] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    if (profile) {
      setSbUsername(profile.statsbomb_username || '');
      if (profile.statsbomb_verified) {
        setConnectionStatus(STATUS.VALID);
        setStatusMessage('StatsBomb credentials verified.');
      }
    }
  }, [profile]);

  const handleTestConnection = async () => {
    if (!sbUsername.trim() || !sbToken.trim()) {
      setStatusMessage('Both username and token are required.');
      setConnectionStatus(STATUS.INVALID);
      return;
    }

    setConnectionStatus(STATUS.TESTING);
    setStatusMessage('Verifying StatsBomb credentials...');

    const result = await testStatsBombConnection(sbUsername, sbToken);

    if (result.success) {
      setConnectionStatus(STATUS.VALID);
      setStatusMessage(result.message);
    } else {
      setConnectionStatus(STATUS.INVALID);
      setStatusMessage(result.message);
    }
  };

  const handleSave = () => {
    const updates = {
      statsbomb_username: sbUsername,
    };

    if (sbToken.trim()) {
      updates.statsbomb_token_encrypted = sbToken;
      updates.statsbomb_token_last4 = sbToken.slice(-4);
      updates.statsbomb_verified = connectionStatus === STATUS.VALID;
    }

    onSave(updates);
  };

  const getMaskedToken = () => {
    if (!profile?.statsbomb_token_last4) return '';
    return `${'*'.repeat(24)}${profile.statsbomb_token_last4}`;
  };

  return (
    <div>
      {/* StatsBomb Section */}
      <div style={s.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#1a1a2e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#e94560',
            fontWeight: '700',
            fontSize: '14px',
            border: '2px solid #e94560',
          }}>
            SB
          </div>
          <div>
            <h3 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: '700', margin: 0 }}>
              StatsBomb Data
            </h3>
            <p style={{ color: '#64748b', fontSize: '12px', margin: 0 }}>
              Connect your StatsBomb account for advanced match data and event analytics.
            </p>
          </div>
        </div>

        {/* Username */}
        <div style={s.fieldGroup}>
          <label style={s.label}>Username</label>
          <input
            type="text"
            value={sbUsername}
            onChange={(e) => {
              setSbUsername(e.target.value);
              if (connectionStatus !== STATUS.UNTESTED) {
                setConnectionStatus(STATUS.UNTESTED);
                setStatusMessage('');
              }
            }}
            onFocus={() => setFocusedField('sbUsername')}
            onBlur={() => setFocusedField(null)}
            style={focusedField === 'sbUsername' ? s.inputFocused : s.input}
            placeholder="Enter your StatsBomb username"
          />
        </div>

        {/* Token */}
        <div style={s.fieldGroup}>
          <label style={s.label}>API Token</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showToken ? 'text' : 'password'}
              value={sbToken}
              onChange={(e) => {
                setSbToken(e.target.value);
                if (connectionStatus !== STATUS.UNTESTED) {
                  setConnectionStatus(STATUS.UNTESTED);
                  setStatusMessage('');
                }
              }}
              onFocus={() => setFocusedField('sbToken')}
              onBlur={() => setFocusedField(null)}
              style={focusedField === 'sbToken' ? s.inputFocused : s.input}
              placeholder="Enter your StatsBomb API token"
            />
            <button
              type="button"
              onClick={() => setShowToken(!showToken)}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#64748b',
                cursor: 'pointer',
                padding: '4px',
              }}
            >
              {showToken ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Saved token display */}
        {profile?.statsbomb_token_last4 && !sbToken && (
          <div style={{
            padding: '10px 14px',
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span style={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'monospace' }}>
              {getMaskedToken()}
            </span>
          </div>
        )}

        {/* Connection Status */}
        {statusMessage && (
          <div style={{
            padding: '10px 14px',
            background: statusConfig[connectionStatus].bg,
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: statusConfig[connectionStatus].color,
              ...(connectionStatus === STATUS.TESTING ? {
                animation: 'pulse-sb 1.5s ease-in-out infinite',
              } : {}),
            }} />
            <span style={{
              color: statusConfig[connectionStatus].color,
              fontSize: '13px',
              fontWeight: '500',
            }}>
              {statusMessage}
            </span>
          </div>
        )}

        <style>{`
          @keyframes pulse-sb {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.3); }
          }
        `}</style>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={!sbUsername.trim() || !sbToken.trim() || connectionStatus === STATUS.TESTING}
            style={{
              ...s.btnSecondary,
              opacity: !sbUsername.trim() || !sbToken.trim() || connectionStatus === STATUS.TESTING ? 0.5 : 1,
              cursor: !sbUsername.trim() || !sbToken.trim() || connectionStatus === STATUS.TESTING ? 'not-allowed' : 'pointer',
            }}
          >
            {connectionStatus === STATUS.TESTING ? 'Testing...' : 'Test Connection'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              ...s.btnPrimary,
              opacity: saving ? 0.6 : 1,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving...' : 'Save Credentials'}
          </button>
        </div>
      </div>

      {/* Coming Soon Section */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>
          More Integrations
        </h3>
        <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
          These data providers are coming soon to TAHLEEL.ai.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {COMING_SOON.map((item) => (
            <div key={item.name} style={s.comingSoonCard}>
              <div style={s.comingSoonBadge}>Coming Soon</div>
              <div style={s.iconCircle(item.color)}>
                {item.icon}
              </div>
              <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                {item.name}
              </div>
              <div style={{ color: '#64748b', fontSize: '12px', lineHeight: '1.4' }}>
                {item.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default APIKeysTab;
